import { useCallback, useState } from "react";

import { createDefaultMutationHook } from "hooks/useMutationHandler";
import { IntentResourceOperation, IntentResourceOperationReviewDecision } from "types/generated";

import useCanReviewIntentResourceOperation from "../access/useCanReviewIntentResourceOperation";
import useIntentProject from "../useIntentProject";
import { SEARCH_INTENT_RESOURCES_OPERATIONS } from "../useSearchIntentResourcesOperations/gql";
import { REVIEW_INTENT_RESOURCE_OPERATION } from "./gql";

const reviewIntentResourceOperationHook = createDefaultMutationHook(
  REVIEW_INTENT_RESOURCE_OPERATION
);

const useIntentResourceOperationReview = (
  operation: IntentResourceOperation,
  intentProjectId: string | undefined
) => {
  const { intentProject } = useIntentProject(intentProjectId);
  const canReview = useCanReviewIntentResourceOperation(intentProject);

  const [reviewMutation, { loading }] = reviewIntentResourceOperationHook({
    refetchQueries: [SEARCH_INTENT_RESOURCES_OPERATIONS],
  });

  const [decision, setDecision] = useState<IntentResourceOperationReviewDecision>();

  const review = useCallback(
    (nextDecision: IntentResourceOperationReviewDecision, note?: string, callback?: () => void) => {
      if (!intentProject) return;

      setDecision(nextDecision);
      reviewMutation(
        {
          intentProject: intentProject.id,
          operationID: operation.id,
          decision: nextDecision,
          note: note || null,
        },
        (data) => {
          if (data?.intentResourceOperationReview.id) {
            callback?.();
          }
        }
      );
    },
    [intentProject, operation.id, reviewMutation]
  );

  const approve = useCallback(
    (note?: string, callback?: () => void) =>
      review(IntentResourceOperationReviewDecision.Allow, note, callback),
    [review]
  );

  const reject = useCallback(
    (note?: string, callback?: () => void) =>
      review(IntentResourceOperationReviewDecision.Deny, note, callback),
    [review]
  );

  return {
    canReview,
    approve,
    reject,
    isApproving: loading && decision === IntentResourceOperationReviewDecision.Allow,
    isRejecting: loading && decision === IntentResourceOperationReviewDecision.Deny,
    actionsDisabled: loading || !canReview.result,
  };
};

export default useIntentResourceOperationReview;
